import React, { useEffect, useState } from "react";
import { Loader2, Users, Radio, CalendarDays, Music4, HandHeart, Megaphone, Mail, TrendingUp, DollarSign, MessageCircle, Heart, Headphones, Compass, Play, Check, X } from "lucide-react";
import api from "@/lib/api";

const fmt = (n) => (n ?? 0).toLocaleString();

function StatCard({ icon: Icon, label, value, sub, tint = "#E6B34A", testid }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-[#141C42] p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${tint}22` }}>
          <Icon className="w-5 h-5" style={{ color: tint }} />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-display font-black tracking-tight leading-none">{value}</p>
          <p className="overline text-zinc-400 mt-1 truncate">{label}</p>
        </div>
      </div>
      {sub && <p className="text-xs text-zinc-500 mt-3">{sub}</p>}
    </div>
  );
}

function Bar({ label, value, max, tint = "#E6B34A" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-zinc-400 w-20 shrink-0 truncate">{label}</span>
      <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: tint }} />
      </div>
      <span className="text-xs text-zinc-300 tabular-nums w-10 text-right shrink-0">{fmt(value)}</span>
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchData = () => api.get("/admin/analytics").then((r) => { if (alive) { setData(r.data); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    fetchData();
    api.get("/admin/tour-stats").then((r) => { if (alive) setTour(r.data); }).catch(() => {});
    const t = setInterval(fetchData, 15000); // keep "listening now" fresh
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#E6B34A]" /></div>;
  if (!data) return <p className="text-zinc-500 py-16 text-center border border-dashed border-white/10 rounded-2xl">Could not load analytics.</p>;

  const { users, content, ads, engagement, listening } = data;
  const maxDay = Math.max(1, ...users.signups_by_day.map((d) => d.count));
  const dayName = (iso) => new Date(iso + "T00:00").toLocaleDateString(undefined, { weekday: "short" });
  const maxStation = Math.max(1, ...((listening?.top_stations) || []).map((s) => s.count));

  return (
    <div className="space-y-8" data-testid="admin-analytics">
      {/* Live listening now */}
      <div className="grid lg:grid-cols-3 gap-6" data-testid="analytics-live">
        <div className="rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-[#0E2A22] to-[#141C42] p-6 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
            </span>
            <span className="overline text-emerald-300">Listening now</span>
          </div>
          <p className="text-5xl font-display font-black tracking-tighter leading-none" data-testid="listening-now">{fmt(listening?.now)}</p>
          <p className="text-xs text-zinc-400 mt-2">
            <Headphones className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
            {fmt(listening?.on_live_show)} on the live show · updates every 15s
          </p>
        </div>
        <div className="lg:col-span-2 rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="analytics-top-stations">
          <div className="flex items-center gap-2 mb-4"><Radio className="w-5 h-5 text-[#E6B34A]" /><h3 className="font-display font-bold text-lg">Top stations right now</h3></div>
          {(listening?.top_stations || []).length === 0 ? (
            <p className="text-sm text-zinc-500 py-6 text-center">No active listeners at the moment.</p>
          ) : (
            <div className="space-y-3">
              {listening.top_stations.map((s, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`top-station-${i}`}>
                  <span className="text-xs text-zinc-300 w-40 shrink-0 truncate">{s.station_name}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full bg-[#E6B34A] transition-all" style={{ width: `${Math.round((s.count / maxStation) * 100)}%` }} />
                  </div>
                  <span className="text-xs text-zinc-300 tabular-nums w-10 text-right shrink-0">{fmt(s.count)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-users" icon={Users} label="Total members" value={fmt(users.total)} sub={`+${fmt(users.new_7d)} this week · +${fmt(users.new_30d)} this month`} />
        <StatCard testid="stat-revenue" icon={DollarSign} label="Ad revenue" value={`$${fmt(ads.revenue)}`} sub={`${fmt(ads.paid_orders)} paid orders`} tint="#34D399" />
        <StatCard testid="stat-events" icon={CalendarDays} label="Upcoming events" value={fmt(content.events_upcoming)} sub={`${fmt(content.events_pending)} awaiting review`} tint="#8CA6FF" />
        <StatCard testid="stat-newsletter" icon={Mail} label="Newsletter subs" value={fmt(content.newsletter)} tint="#F472B6" />
      </div>

      {/* Signups trend */}
      <div className="rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="analytics-signups">
        <div className="flex items-center gap-2 mb-5">
          <TrendingUp className="w-5 h-5 text-[#E6B34A]" />
          <h3 className="font-display font-bold text-lg">New members — last 7 days</h3>
        </div>
        <div className="flex items-end justify-between gap-2 h-40">
          {users.signups_by_day.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
              <span className="text-xs text-zinc-300 tabular-nums">{d.count}</span>
              <div className="w-full rounded-t-lg bg-[#E6B34A] transition-all" style={{ height: `${Math.max(4, (d.count / maxDay) * 100)}%`, minHeight: 4 }} title={`${d.count} on ${d.date}`} />
              <span className="text-[10px] text-zinc-500">{dayName(d.date)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Members by role */}
        <div className="rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="analytics-roles">
          <h3 className="font-display font-bold text-lg mb-5">Members by role</h3>
          <div className="space-y-3">
            <Bar label="Listeners" value={users.listeners} max={users.total} tint="#E6B34A" />
            <Bar label="Hosts" value={users.hosts} max={users.total} tint="#8CA6FF" />
            <Bar label="Admins" value={users.admins} max={users.total} tint="#34D399" />
          </div>
        </div>

        {/* Ad spots */}
        <div className="rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="analytics-ads">
          <div className="flex items-center gap-2 mb-5"><Megaphone className="w-5 h-5 text-[#E6B34A]" /><h3 className="font-display font-bold text-lg">Advertising</h3></div>
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-xl font-display font-black">{fmt(ads.total)}</p><p className="overline text-zinc-400 mt-1">Total spots</p></div>
            <div><p className="text-xl font-display font-black text-emerald-400">{fmt(ads.scheduled)}</p><p className="overline text-zinc-400 mt-1">On air</p></div>
            <div><p className="text-xl font-display font-black text-[#E6B34A]">{fmt(ads.pending_review)}</p><p className="overline text-zinc-400 mt-1">Awaiting review</p></div>
            <div><p className="text-xl font-display font-black text-emerald-400">${fmt(ads.revenue)}</p><p className="overline text-zinc-400 mt-1">Revenue</p></div>
          </div>
        </div>
      </div>

      {/* Content library */}
      <div className="rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="analytics-content">
        <h3 className="font-display font-bold text-lg mb-5">Content & community</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <MiniStat icon={Radio} label="Stations" value={content.stations} />
          <MiniStat icon={Music4} label="Episodes" value={content.episodes} />
          <MiniStat icon={CalendarDays} label="Published events" value={content.events_approved} />
          <MiniStat icon={Music4} label="Song requests" value={content.requests_total} sub={`${fmt(content.requests_played)} played`} />
          <MiniStat icon={HandHeart} label="Prayer requests" value={content.prayers_total} sub={`${fmt(content.prayers_public)} on wall`} tint="#F472B6" />
          <MiniStat icon={Heart} label="Favorites saved" value={engagement.favorites} tint="#FF7A70" />
          <MiniStat icon={MessageCircle} label="Chat messages" value={engagement.chat_messages} tint="#8CA6FF" />
          <MiniStat icon={HandHeart} label="Prayers offered" value={engagement.prayer_prays} tint="#34D399" />
        </div>
      </div>

      {/* Onboarding tour */}
      {tour && (
        <div data-testid="analytics-tour">
          <div className="flex items-center gap-2 mb-4"><Compass className="w-5 h-5 text-[#E6B34A]" /><h3 className="font-display font-bold text-lg">New-user tour</h3></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MiniStat icon={Play} label="Tours started" value={tour.started} />
            <MiniStat icon={Check} label="Finished" value={tour.finished} tint="#34D399" />
            <MiniStat icon={X} label="Skipped" value={tour.skipped} tint="#FF7A70" />
            <MiniStat icon={TrendingUp} label="Finish rate" value={`${tour.finish_rate}%`} tint="#8CA6FF" />
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, sub, tint = "#E6B34A" }) {
  return (
    <div className="rounded-xl bg-[#0C1338] border border-white/5 p-4">
      <Icon className="w-4 h-4 mb-2" style={{ color: tint }} />
      <p className="text-xl font-display font-black tracking-tight">{fmt(value)}</p>
      <p className="overline text-zinc-400 mt-0.5 truncate">{label}</p>
      {sub && <p className="text-[11px] text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}
